/**
 * CatalogPop Front-or-Back print side (schema v1).
 *
 * Reads the product's catalogpop.print_side_v1 manifest (rendered by
 * snippets/catalogpop-print-side.liquid) and offers Front Print / Back Print
 * only for a variant the manifest marks Back-capable. The choice is written as
 * ONE line item property on the product form:
 *
 *   properties[catalogpop_print_side_v1] = FRONT | BACK
 *
 * - Exactly FRONT or BACK. Never Both, never a fee, never a variant.
 * - FRONT is the default and the first paint.
 * - The property is sent only while the selector is offered for the variant
 *   being added; a Front-only variant sends nothing (FRONT by contract).
 * - Shopper intent only: no Store, provider, recipe or catalog data.
 * - Anything malformed or inconsistent degrades silently to Front only.
 *
 * Variant changes come from the theme's own pub/sub event
 * (PUB_SUB_EVENTS.variantChange, published by <high-variant-selects> after it
 * has updated the form's variant input and the gallery). The submitted variant
 * is always read from the form's own input[name="id"].
 *
 * Shareable links (navigation convenience only, never authority):
 *
 *   ?cp_color=<exact Color option value>&cp_side=FRONT|BACK
 *
 * - cp_color is matched exactly (or case-insensitively when that is unique)
 *   against the product's own Color values and selected through the theme's
 *   own picker, which fetches and renders it as a click would. Never fuzzy,
 *   never a guess, never a Size, never a forged variant id.
 * - cp_side=BACK selects Back only where the manifest allows it. Before a
 *   Size is chosen that means every size of the Color is Back-capable with
 *   the same Back images; once a Size is chosen, the exact variant decides,
 *   and the submit check always uses the exact variant.
 * - The canonical state is written back with history.replaceState: unrelated
 *   query parameters keep their exact bytes, Size is never added.
 */
(function () {
  'use strict';

  var LINE_ATTRIBUTE_KEY = 'catalogpop_print_side_v1';
  var VARIANT_GID = /^gid:\/\/shopify\/ProductVariant\/[0-9A-Za-z]{1,64}$/;
  var MEDIA_GID = /^gid:\/\/shopify\/MediaImage\/[0-9A-Za-z]{1,64}$/;
  var MAX_VARIANTS = 2048;
  var MAX_MEDIA_PER_SIDE = 10;
  var SHOWN_ATTRIBUTE = 'data-cp-print-side-shown';
  var COLOR_PARAM = 'cp_color';
  var SIDE_PARAM = 'cp_side';
  var COLOR_OPTION_NAME = /^colou?r$/i;
  var MAX_PARAM_LENGTH = 255;
  var PENDING_COLOR_TIMEOUT_MS = 10000;

  function isPlainObject(value) {
    return Object.prototype.toString.call(value) === '[object Object]';
  }

  function hasExactKeys(object, keys) {
    var own = Object.keys(object);
    if (own.length !== keys.length) return false;
    for (var i = 0; i < keys.length; i++) {
      if (!Object.prototype.hasOwnProperty.call(object, keys[i])) return false;
    }
    return true;
  }

  function isMediaList(value) {
    if (!Array.isArray(value) || value.length > MAX_MEDIA_PER_SIDE) return false;
    for (var i = 0; i < value.length; i++) {
      if (typeof value[i] !== 'string' || !MEDIA_GID.test(value[i])) return false;
    }
    return true;
  }

  function numericId(gid) {
    return gid.slice(gid.lastIndexOf('/') + 1);
  }

  /**
   * Schema v1, strictly (mirrors @catalogpop/shared printSideManifestSchema),
   * plus product ownership: every variant must be one of this product's
   * variants and every image one of its image media. Returns null otherwise.
   */
  function parseManifest(manifest, product) {
    if (!isPlainObject(manifest)) return null;
    if (!hasExactKeys(manifest, ['schemaVersion', 'lineAttributeKey', 'defaultSide', 'variants'])) return null;
    if (manifest.schemaVersion !== 1) return null;
    if (manifest.lineAttributeKey !== LINE_ATTRIBUTE_KEY) return null;
    if (manifest.defaultSide !== 'FRONT') return null;
    if (!isPlainObject(manifest.variants)) return null;
    if (!isPlainObject(product) || !Array.isArray(product.variants) || !isPlainObject(product.media)) return null;

    var gids = Object.keys(manifest.variants);
    if (gids.length < 1 || gids.length > MAX_VARIANTS) return null;

    var productVariants = {};
    for (var v = 0; v < product.variants.length; v++) productVariants[String(product.variants[v])] = true;

    var variants = {};
    var backMedia = [];
    for (var i = 0; i < gids.length; i++) {
      var gid = gids[i];
      var entry = manifest.variants[gid];
      if (!VARIANT_GID.test(gid) || !productVariants[numericId(gid)]) return null;
      if (!isPlainObject(entry) || !hasExactKeys(entry, ['sides', 'frontMedia', 'backMedia'])) return null;
      if (!isMediaList(entry.frontMedia) || !isMediaList(entry.backMedia)) return null;

      var sides = entry.sides;
      var back;
      if (Array.isArray(sides) && sides.length === 1 && sides[0] === 'FRONT') back = false;
      else if (Array.isArray(sides) && sides.length === 2 && sides[0] === 'FRONT' && sides[1] === 'BACK') back = true;
      else return null;
      if (back && entry.backMedia.length === 0) return null;
      if (!back && entry.backMedia.length > 0) return null;

      var all = entry.frontMedia.concat(entry.backMedia);
      var seen = {};
      for (var m = 0; m < all.length; m++) {
        var id = numericId(all[m]);
        if (seen[id] || !Object.prototype.hasOwnProperty.call(product.media, id)) return null;
        seen[id] = true;
      }

      variants[numericId(gid)] = {
        back: back,
        frontMedia: entry.frontMedia.map(numericId),
        backMedia: entry.backMedia.map(numericId),
      };
      for (var b = 0; b < entry.backMedia.length; b++) {
        var backId = numericId(entry.backMedia[b]);
        if (backMedia.indexOf(backId) < 0) backMedia.push(backId);
      }
    }
    return { variants: variants, backMedia: backMedia };
  }

  function readJson(root, selector) {
    var node = root.querySelector(selector);
    if (!node) return null;
    try {
      return JSON.parse(node.textContent);
    } catch (e) {
      return null;
    }
  }

  /**
   * The product's options as rendered by the snippet: option names, each
   * variant's option values, and the one option named Color (or Colour), if
   * exactly one is. Returns null when the data is missing or inconsistent.
   */
  function optionModel(product) {
    if (!isPlainObject(product) || !Array.isArray(product.variants)) return null;
    if (!Array.isArray(product.options) || !isPlainObject(product.variantOptions)) return null;
    var names = product.options.map(String);
    var byVariant = {};
    var ids = [];
    for (var v = 0; v < product.variants.length; v++) {
      var id = String(product.variants[v]);
      var values = product.variantOptions[id];
      if (!Array.isArray(values) || values.length !== names.length) return null;
      byVariant[id] = values.map(String);
      ids.push(id);
    }
    var colorIndex = -1;
    for (var n = 0; n < names.length; n++) {
      if (!COLOR_OPTION_NAME.test(names[n].trim())) continue;
      if (colorIndex >= 0) {
        colorIndex = -1; // two Color options: ambiguous, so no Color links
        break;
      }
      colorIndex = n;
    }
    var colorValues = [];
    if (colorIndex >= 0) {
      for (var i = 0; i < ids.length; i++) {
        var value = byVariant[ids[i]][colorIndex];
        if (colorValues.indexOf(value) < 0) colorValues.push(value);
      }
    }
    return { names: names, ids: ids, byVariant: byVariant, colorIndex: colorIndex, colorValues: colorValues };
  }

  function nfc(value) {
    return (typeof value.normalize === 'function' ? value.normalize('NFC') : value).trim();
  }

  /**
   * The product's own Color value a link names: an exact match, else the one
   * case-insensitive match. Never a substring, never one of several.
   */
  function matchColor(model, raw) {
    if (!model || model.colorIndex < 0 || typeof raw !== 'string' || raw.length > MAX_PARAM_LENGTH) return null;
    var wanted = nfc(raw);
    if (!wanted) return null;
    var exact = [];
    var folded = [];
    model.colorValues.forEach(function (value) {
      var candidate = nfc(value);
      if (candidate === wanted) exact.push(value);
      else if (candidate.toLowerCase() === wanted.toLowerCase()) folded.push(value);
    });
    if (exact.length === 1) return exact[0];
    if (exact.length === 0 && folded.length === 1) return folded[0];
    return null;
  }

  /** FRONT or BACK, ASCII case-insensitively; anything else is no request. */
  function parseSideParam(raw) {
    if (typeof raw !== 'string' || !/^[A-Za-z]{4,5}$/.test(raw)) return null;
    var side = raw.toUpperCase();
    return side === 'FRONT' || side === 'BACK' ? side : null;
  }

  function decodeComponent(raw) {
    try {
      return decodeURIComponent(raw.replace(/\+/g, ' '));
    } catch (e) {
      return null;
    }
  }

  function querySegments(query) {
    return query ? query.split('&').filter(Boolean) : [];
  }

  function segmentKey(segment) {
    var at = segment.indexOf('=');
    return decodeComponent(at < 0 ? segment : segment.slice(0, at));
  }

  function splitHref(href) {
    var hashAt = href.indexOf('#');
    var hash = hashAt < 0 ? '' : href.slice(hashAt);
    var rest = hashAt < 0 ? href : href.slice(0, hashAt);
    var queryAt = rest.indexOf('?');
    return {
      base: queryAt < 0 ? rest : rest.slice(0, queryAt),
      query: queryAt < 0 ? '' : rest.slice(queryAt + 1),
      hash: hash,
    };
  }

  /**
   * The value of `name` in a query string: undefined when absent, null when
   * repeated with different values or undecodable, else the decoded string.
   */
  function readParam(search, name) {
    var found;
    var segments = querySegments(search.charAt(0) === '?' ? search.slice(1) : search);
    for (var i = 0; i < segments.length; i++) {
      if (segmentKey(segments[i]) !== name) continue;
      var at = segments[i].indexOf('=');
      var value = at < 0 ? '' : decodeComponent(segments[i].slice(at + 1));
      if (value === null || (found !== undefined && found !== value)) return null;
      found = value;
    }
    return found;
  }

  /**
   * `href` with exactly one cp_color / cp_side for `state` (null drops it).
   * Every other query segment keeps its exact bytes and order. When the theme's
   * own updateURL() has just replaced the whole query with ?variant=<id> (and
   * dropped the hash), the parameters and hash of `knownHref` are carried over
   * with the theme's new variant.
   */
  function canonicalHref(href, knownHref, state) {
    var url = splitHref(href);
    var segments = querySegments(url.query);
    var hash = url.hash;
    if (knownHref && knownHref !== href && /^variant=\d+$/.test(url.query) && url.hash === '') {
      var known = splitHref(knownHref);
      var placed = false;
      segments = [];
      querySegments(known.query).forEach(function (segment) {
        if (segmentKey(segment) !== 'variant') segments.push(segment);
        else if (!placed) {
          segments.push(url.query);
          placed = true;
        }
      });
      if (!placed) segments.push(url.query);
      hash = known.hash;
    }
    segments = segments.filter(function (segment) {
      var key = segmentKey(segment);
      return key !== COLOR_PARAM && key !== SIDE_PARAM;
    });
    if (state.color != null) segments.push(COLOR_PARAM + '=' + encodeURIComponent(state.color));
    if (state.side != null) segments.push(SIDE_PARAM + '=' + state.side);
    return url.base + (segments.length ? '?' + segments.join('&') : '') + hash;
  }

  // The one selector per document that reads and writes the page URL.
  var urlOwner = null;

  function init(root) {
    if (root.cpPrintSideReady) return;
    root.cpPrintSideReady = true;

    var sectionId = root.dataset.section;
    var form = document.getElementById(root.dataset.formId);
    var input = root.querySelector('[data-cp-print-side-input]');
    var radios = root.querySelectorAll('.cp-print-side__input');
    if (!sectionId || !form) return;

    var product = readJson(root, '[data-cp-print-side-product]');
    var options = optionModel(product);
    // No valid manifest (or none rendered): no selector, no property, Front only.
    // Color links still work.
    var manifest =
      input && radios.length === 2 ? parseManifest(readJson(root, '[data-cp-print-side-manifest]'), product) : null;

    var side = 'FRONT';
    var renderedVariantId = null;
    var revealed = '';
    var featuredMediaId = initialFeaturedMediaId(sectionId);
    // True once the shopper has picked a Size for this form (size-selection-required.js
    // keeps Size unselected until then, so the form's variant is only Shopify's default).
    var sizeChosen = false;
    // A linked Color the theme is still fetching and rendering: { color, side, timer, scrollY }.
    var pending = null;
    var ownsUrl = false;
    var urlActive = false;
    var writeColor = false;
    var knownHref = null;
    var installWatch = null;

    function variantInput() {
      return form.querySelector('input[name="id"]');
    }

    function currentEntry() {
      var idInput = variantInput();
      var id = idInput ? String(idInput.value) : '';
      return {
        id: id,
        entry: manifest && Object.prototype.hasOwnProperty.call(manifest.variants, id) ? manifest.variants[id] : null,
      };
    }

    function picker() {
      return document.getElementById('Variant-Selects-' + sectionId);
    }

    function sizeFieldset() {
      var element = picker();
      return element ? element.querySelector('[data-is-size-option]') : null;
    }

    function awaitingSize() {
      return !sizeChosen && !!sizeFieldset();
    }

    function sizeOptionIndex() {
      var fieldset = sizeFieldset();
      var control = fieldset && fieldset.querySelector('input[type="radio"], select');
      if (!control || !options) return -1;
      var name = control.tagName === 'SELECT' ? control.name.replace(/^options\[(.*)\]$/, '$1') : control.name;
      return options.names.indexOf(name);
    }

    /**
     * Before a Size is chosen, Back is offered only if EVERY variant that differs
     * from the form's variant by Size alone is Back-capable with the same Back
     * images. A Front-only size, different Back images or missing option data
     * mean Front only until an exact Size is chosen.
     */
    function colorGroupEntry(variantId) {
      var sizeIndex = sizeOptionIndex();
      var own = options && options.byVariant[variantId];
      var ownEntry = Object.prototype.hasOwnProperty.call(manifest.variants, variantId) ? manifest.variants[variantId] : null;
      if (sizeIndex < 0 || !own || !ownEntry || !ownEntry.back) return null;
      var backKey = ownEntry.backMedia.join(',');
      for (var i = 0; i < options.ids.length; i++) {
        var values = options.byVariant[options.ids[i]];
        var sameColorGroup = true;
        for (var k = 0; k < values.length; k++) {
          if (k !== sizeIndex && values[k] !== own[k]) sameColorGroup = false;
        }
        if (!sameColorGroup) continue;
        var id = options.ids[i];
        var entry = Object.prototype.hasOwnProperty.call(manifest.variants, id) ? manifest.variants[id] : null;
        if (!entry || !entry.back || entry.backMedia.join(',') !== backKey) return null;
      }
      return ownEntry;
    }

    /** The Back-capable entry the selector may offer now, else null. `exact`: the form's variant only. */
    function offeredEntry(exact) {
      if (!manifest) return null;
      var current = currentEntry();
      if (exact || !awaitingSize()) return current.entry && current.entry.back ? current.entry : null;
      return colorGroupEntry(current.id);
    }

    function setThumb(which, mediaId) {
      var img = root.querySelector('[data-cp-print-side-thumb="' + which + '"]');
      if (!img) return;
      var url = mediaId && Object.prototype.hasOwnProperty.call(product.media, mediaId) ? product.media[mediaId] : null;
      if (url) {
        if (img.getAttribute('src') !== url) img.setAttribute('src', url);
        img.hidden = false;
      } else {
        img.removeAttribute('src');
        img.hidden = true;
      }
    }

    function galleryNodes(mediaId) {
      var nodes = [];
      var slide = document.getElementById('Slide-' + sectionId + '-' + mediaId);
      if (slide) nodes.push(slide);
      var thumbs = document.getElementById('Slider-Thumbnails-' + sectionId);
      if (thumbs) thumbs.querySelectorAll('[data-target="' + mediaId + '"]').forEach(function (n) { nodes.push(n); });
      var modal = document.getElementById('ProductModal-' + sectionId);
      if (modal) modal.querySelectorAll('[data-media-id="' + mediaId + '"]').forEach(function (n) { nodes.push(n); });
      return nodes;
    }

    /** Shows exactly `reveal` of the product's Back images; returns whether anything changed. */
    function revealBackMedia(reveal) {
      var key = reveal.join(',');
      if (key === revealed) return false;
      revealed = key;
      manifest.backMedia.forEach(function (mediaId) {
        var show = reveal.indexOf(mediaId) >= 0;
        galleryNodes(mediaId).forEach(function (node) {
          node.toggleAttribute(SHOWN_ATTRIBUTE, show);
        });
      });
      return true;
    }

    /**
     * The theme's own slider calls: re-measure the visible slides, then move
     * without scrolling the page. Like media-gallery.js updateGallery(), a
     * re-install is ALWAYS followed by an explicit slide: a re-install alone
     * settles on slide 0, and the theme's swipe-to-select script
     * (media-gallery-variant-swipe.js) would then select that photo's colour.
     * Order: the requested slide, else the slide that was current (if still
     * shown), else the variant's own front media.
     */
    function refreshGallery(changed, targetMediaId) {
      var viewer = document.getElementById('Gallery-Viewer-' + sectionId);
      var thumbnails = document.getElementById('Gallery-Thumbnail-Slider-' + sectionId);
      if (!viewer) return;
      var before = viewer.initSliderStatus && viewer.sliderItems ? viewer.sliderItems[viewer.index] : null;
      var beforeId = before && before.dataset ? before.dataset.mediaId : null;
      if (changed) {
        if (typeof viewer.reInstall === 'function') viewer.reInstall();
        if (thumbnails && typeof thumbnails.reInstall === 'function') thumbnails.reInstall();
      }
      if (!viewer.initSliderStatus || typeof viewer.slideByElement !== 'function') {
        slideWhenInstalled(viewer);
        return;
      }
      var candidates = [targetMediaId, beforeId, frontTargetId(currentEntry().entry)];
      for (var i = 0; i < candidates.length; i++) {
        if (!candidates[i]) continue;
        var slide = document.getElementById('Slide-' + sectionId + '-' + candidates[i]);
        if (slide && viewer.slideByElement(slide)) return;
      }
    }

    /**
     * A linked Back chosen before the slider has installed: once it installs
     * (it lands on slide 0), move it to the Back image.
     */
    function slideWhenInstalled(viewer) {
      if (installWatch || typeof MutationObserver === 'undefined' || typeof viewer.slideByElement !== 'function') return;
      installWatch = new MutationObserver(function () {
        if (!viewer.initSliderStatus) return;
        installWatch.disconnect();
        installWatch = null;
        var entry = offeredEntry(false);
        if (side === 'BACK' && entry) refreshGallery(false, entry.backMedia[0]);
      });
      installWatch.observe(viewer, { attributes: true, attributeFilter: ['class'] });
    }

    function frontTargetId(entry) {
      var viewer = document.getElementById('Gallery-Viewer-' + sectionId);
      var current = viewer && viewer.querySelector('.is-current-variant-media');
      if (current && current.dataset.mediaId) return current.dataset.mediaId;
      return entry && entry.frontMedia[0] ? entry.frontMedia[0] : featuredMediaId;
    }

    /**
     * Brings the selector, the property and the gallery in line with the
     * variant in the form. `navigate` moves the gallery to the chosen side.
     */
    function sync(navigate, exact) {
      if (!manifest) return;
      var current = currentEntry();
      var entry = offeredEntry(exact);
      var offered = !!entry;
      var variantChanged = current.id !== renderedVariantId;
      renderedVariantId = current.id;

      // A variant without Back always resets to Front; nothing stale survives it.
      if (!offered) side = 'FRONT';

      root.hidden = !offered;
      input.disabled = !offered;
      input.value = side;
      root.dataset.cpPrintSideState = offered ? side : 'FRONT_ONLY';
      radios.forEach(function (radio) {
        radio.checked = radio.value === side;
      });

      if (offered) {
        setThumb('FRONT', entry.frontMedia[0] || featuredMediaId);
        setThumb('BACK', entry.backMedia[0]);
      }

      var showBack = offered && side === 'BACK';
      var changed = revealBackMedia(showBack ? entry.backMedia : []);
      if (showBack && (navigate || variantChanged || changed)) {
        refreshGallery(changed, entry.backMedia[0]);
      } else if (!showBack && navigate) {
        refreshGallery(changed, frontTargetId(entry));
      } else if (changed) {
        refreshGallery(changed, null);
      }
    }

    radios.forEach(function (radio) {
      radio.addEventListener('change', function () {
        if (!radio.checked) return;
        side = radio.value === 'BACK' && offeredEntry(false) ? 'BACK' : 'FRONT';
        if (pending) pending.side = null; // the shopper's own choice wins over the link
        sync(true);
        shopperChanged();
      });
    });

    // Re-check against the form's own EXACT variant at the moment it is submitted.
    document.addEventListener(
      'submit',
      function (event) {
        if (event.target === form) sync(false, true);
      },
      true,
    );

    // Mirrors size-selection-required.js: a change inside this form's Size fieldset is a chosen Size.
    document.addEventListener(
      'change',
      function (event) {
        var target = event.target;
        if (!root.isConnected || !target || typeof target.closest !== 'function') return;
        if (target.closest('[data-is-size-option]') && target.getAttribute('form') === root.dataset.formId) {
          sizeChosen = true;
        }
      },
      true,
    );

    function currentColor() {
      if (!options || options.colorIndex < 0) return null;
      var idInput = variantInput();
      var values = idInput ? options.byVariant[String(idInput.value)] : null;
      return values ? values[options.colorIndex] : null;
    }

    /** The theme's own control for Color `value` in this section's picker. */
    function colorControl(value) {
      var element = picker();
      if (!element || !options || options.colorIndex < 0) return null;
      var name = options.names[options.colorIndex];
      var inputs = element.querySelectorAll('input[type="radio"]');
      for (var i = 0; i < inputs.length; i++) {
        if (inputs[i].name === name && inputs[i].value === value && inputs[i].getAttribute('form') === root.dataset.formId) {
          return { radio: inputs[i] };
        }
      }
      var selects = element.querySelectorAll('select');
      for (var s = 0; s < selects.length; s++) {
        if (selects[s].name !== 'options[' + name + ']') continue;
        for (var o = 0; o < selects[s].options.length; o++) {
          if (selects[s].options[o].value === value) return { select: selects[s], option: selects[s].options[o] };
        }
      }
      return null;
    }

    /**
     * Selects Color `value` the way a click does: the theme's own control is
     * checked and <high-variant-selects> handles the change (fetch, re-render,
     * variant input, gallery, variantChange). The event is dispatched on the
     * picker itself, which is what it listens on; dispatched on the swatch,
     * the theme would focus that swatch after re-rendering, giving a focus
     * ring and possibly a scroll at page load. Size is left untouched.
     */
    function chooseColor(control) {
      if (control.radio) {
        control.radio.checked = true;
      } else {
        for (var o = 0; o < control.select.options.length; o++) control.select.options[o].removeAttribute('selected');
        control.option.setAttribute('selected', 'selected');
        control.select.value = control.option.value;
      }
      picker().dispatchEvent(new Event('change', { bubbles: true }));
    }

    // While a linked Color is being fetched, the gallery still shows the old
    // Color. The theme's swipe-to-select script (media-gallery-variant-swipe.js)
    // would read the slider's next change event as a pick of that old Color and
    // undo the link, so that event is held until the new Color has rendered.
    function holdSlideChange(event) {
      event.stopPropagation();
    }

    function holdGallery(on) {
      var gallery = document.getElementById('Media-Gallery-' + sectionId);
      if (!gallery) return;
      if (on) gallery.addEventListener('sliderChanged', holdSlideChange, true);
      else gallery.removeEventListener('sliderChanged', holdSlideChange, true);
    }

    function endPending() {
      if (!pending) return null;
      var ended = pending;
      clearTimeout(ended.timer);
      pending = null;
      holdGallery(false);
      return ended;
    }

    function applyColor(control, color, requestedSide) {
      endPending();
      pending = {
        color: color,
        side: requestedSide,
        timer: setTimeout(endPending, PENDING_COLOR_TIMEOUT_MS),
        scrollY: window.scrollY,
      };
      holdGallery(true);
      chooseColor(control);
    }

    /** BACK only where the current state allows it; everything else is Front. */
    function applySide(requestedSide) {
      if (!manifest) return;
      side = requestedSide === 'BACK' && offeredEntry(false) ? 'BACK' : 'FRONT';
      sync(side === 'BACK');
    }

    function writeUrl() {
      if (!ownsUrl || !urlActive || pending) return;
      var href = window.location.href;
      var next = canonicalHref(href, knownHref, {
        color: writeColor ? currentColor() : null,
        side: offeredEntry(false) ? side : null,
      });
      knownHref = next;
      if (next !== href) window.history.replaceState(window.history.state, '', next);
    }

    function shopperChanged() {
      urlActive = true;
      writeColor = true;
      writeUrl();
    }

    /** Applies this page URL's cp_color / cp_side. `fromHistory`: absent cp_side means Front. */
    function applyUrl(fromHistory) {
      var search = window.location.search;
      var rawColor = readParam(search, COLOR_PARAM);
      var rawSide = readParam(search, SIDE_PARAM);
      if (!fromHistory && rawColor === undefined && rawSide === undefined) return;
      urlActive = true;
      var requestedSide = parseSideParam(rawSide);
      var color = matchColor(options, rawColor);
      var control = color && color !== currentColor() ? colorControl(color) : null;
      var colorUsable = !!color && (color === currentColor() || !!control);
      if (colorUsable) writeColor = true;
      // A Color this product does not have: no guess, and no Back on some other Color either.
      if (rawColor !== undefined && options && options.colorIndex >= 0 && !colorUsable) requestedSide = null;
      if (control) {
        // Side and URL follow once the theme has rendered this Color.
        applyColor(control, color, requestedSide);
        return;
      }
      applySide(requestedSide);
      writeUrl();
    }

    function urlEligible() {
      if (urlOwner || root.dataset.originalSection) return false;
      if (!window.history || typeof window.history.replaceState !== 'function') return false;
      var element = picker();
      if (!element) return true;
      if (element.dataset.updateUrl === 'false' || element.dataset.originalSection) return false;
      var productPath = element.dataset.url;
      var path = window.location.pathname;
      return !productPath || path.slice(-productPath.length) === productPath;
    }

    var unsubscribe = null;
    if (typeof webvista !== 'undefined' && webvista.subscribe && typeof PUB_SUB_EVENTS !== 'undefined') {
      unsubscribe = webvista.subscribe(PUB_SUB_EVENTS.variantChange, function (event) {
        if (!root.isConnected) {
          if (unsubscribe) unsubscribe();
          return;
        }
        if (!event || !event.data || event.data.sectionId !== sectionId) return;
        var variant = event.data.variant;
        if (variant && variant.featured_media && variant.featured_media.id != null) {
          featuredMediaId = String(variant.featured_media.id);
        }
        var linked = endPending();
        sync(false);
        if (!linked) {
          shopperChanged();
          return;
        }
        // On mobile the theme's updateGallery() has just started a smooth scroll to the
        // gallery, which is right for a click but a jump for a link applied at page
        // load. It has not moved yet in this turn; unless the shopper has scrolled
        // meanwhile, an instant scroll to the same place cancels it.
        if (window.scrollY === linked.scrollY) window.scrollTo({ top: linked.scrollY, behavior: 'instant' });
        // The linked Color has rendered; anything else changed it instead: keep the safe default.
        if (currentColor() === linked.color) applySide(linked.side);
        writeUrl();
      });
    }

    sync(false);

    if (urlEligible()) {
      urlOwner = root;
      ownsUrl = true;
      knownHref = window.location.href;
      // The theme only replaces history entries; a same-document entry comes from a #hash link.
      window.addEventListener('popstate', function () {
        if (!root.isConnected) return;
        knownHref = window.location.href;
        applyUrl(true);
      });
      // Only once every theme component is defined: before that the picker's own
      // change handler throws (its <product-form> is not upgraded yet).
      afterDomReady(function () {
        applyUrl(false);
      });
    }
  }

  function afterDomReady(fn) {
    var done = false;
    function once() {
      if (done) return;
      done = true;
      fn();
    }
    var nav = window.performance && performance.getEntriesByType ? performance.getEntriesByType('navigation')[0] : null;
    if (document.readyState === 'complete' || (nav && nav.domContentLoadedEventEnd > 0)) {
      once();
      return;
    }
    document.addEventListener('DOMContentLoaded', once);
    window.addEventListener('load', once);
  }

  function initialFeaturedMediaId(sectionId) {
    var picker = document.getElementById('Variant-Selects-' + sectionId);
    var node = picker && picker.querySelector('script[data-selected-variant]');
    if (!node) return null;
    try {
      var variant = JSON.parse(node.textContent);
      return variant && variant.featured_media && variant.featured_media.id != null
        ? String(variant.featured_media.id)
        : null;
    } catch (e) {
      return null;
    }
  }

  function initAll() {
    document.querySelectorAll('[data-cp-print-side]').forEach(init);
  }

  window.catalogpopPrintSide = {
    parseManifest: parseManifest,
    optionModel: optionModel,
    matchColor: matchColor,
    parseSideParam: parseSideParam,
    readParam: readParam,
    canonicalHref: canonicalHref,
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initAll);
  } else {
    // Re-executed by the theme's quick-add drawer after the page has loaded.
    initAll();
  }
})();
